import { useCallback, useEffect, useRef } from 'react';
import { useMessageStore } from '@/stores/useMessageStore.js';
import { useGisStore } from '@/stores/useGisStore.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { Direction } from '@k-ucap/shared';
import type { Route, MessageInstance, MessageEvent } from '@k-ucap/shared';

const DEMO_INTERVAL_MS = 1000;
const DEMO_MAX_DURATION_MS = 60 * 1000;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const interpolatePosition = (
  from: { latitude: number; longitude: number; altitudeM?: number },
  to: { latitude: number; longitude: number; altitudeM?: number },
  ratio: number
) => ({
  latitude: from.latitude + (to.latitude - from.latitude) * ratio,
  longitude: from.longitude + (to.longitude - from.longitude) * ratio,
  altitudeM: (from.altitudeM ?? 0) + ((to.altitudeM ?? 0) - (from.altitudeM ?? 0)) * ratio,
});

export interface UseDemoFlightOptions {
  route: Route;
  recordToFirestore?: boolean;
}

export const useDemoFlight = ({ route, recordToFirestore = false }: UseDemoFlightOptions) => {
  const addMessage = useMessageStore((s) => s.addMessage);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const authUser = useAuthStore((s) => s.authUser);
  const { messageEventRepository } = useRepositories();
  const demoRunning = useGisStore((s) => s.demoRunning);
  const startDemo = useGisStore((s) => s.startDemo);
  const stopDemo = useGisStore((s) => s.stopDemo);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const seqRef = useRef(1);

  const waypoints = route.waypoints;
  const segmentDists = waypoints.slice(1).map((_, i) => {
    const a = waypoints[i].coordinates;
    const b = waypoints[i + 1].coordinates;
    const latDiff = b.latitude - a.latitude;
    const lonDiff = b.longitude - a.longitude;
    const altDiff = (b.altitudeM ?? 0) - (a.altitudeM ?? 0);
    return Math.sqrt(latDiff * latDiff + lonDiff * lonDiff + altDiff * altDiff);
  });
  const cumulativeDists = segmentDists.reduce(
    (acc, d, i) => {
      acc.push((acc[i] ?? 0) + d);
      return acc;
    },
    [0] as number[]
  );

  const positionAtProgress = useCallback(
    (progress: number) => {
      const target = progress * cumulativeDists[cumulativeDists.length - 1];
      let segment = 0;
      for (let i = 0; i < cumulativeDists.length - 1; i++) {
        if (target >= cumulativeDists[i] && target <= cumulativeDists[i + 1]) {
          segment = i;
          break;
        }
      }
      const segmentStart = cumulativeDists[segment];
      const segmentEnd = cumulativeDists[segment + 1];
      const segmentLen = segmentEnd - segmentStart;
      const ratio = segmentLen > 0 ? (target - segmentStart) / segmentLen : 0;
      return interpolatePosition(waypoints[segment].coordinates, waypoints[segment + 1].coordinates, ratio);
    },
    [cumulativeDists, waypoints]
  );

  const buildInertialMessage = useCallback(
    (position: { latitude: number; longitude: number; altitudeM: number }): MessageInstance => {
      const heading = 90;
      return {
        id: generateId(),
        messageType: 101,
        standard: 'STANAG 4586',
        name: 'Inertial States',
        source: 'AIR_VEHICLE',
        target: 'CUCS',
        direction: Direction.RX,
        timestamp: new Date().toISOString(),
        fields: {
          vehicleId: 'UAV-001',
          cucsId: 'CUCS-01',
          latitude: Number(position.latitude.toFixed(6)),
          longitude: Number(position.longitude.toFixed(6)),
          altitudeM: Math.round(position.altitudeM),
          northVelocityMps: 0,
          eastVelocityMps: 55,
          verticalVelocityMps: 0,
          rollDeg: 0,
          pitchDeg: 0,
          yawDeg: heading,
        },
        projectId: selectedProject?.id,
        environment: selectedProject?.environment ?? 'development',
        sequenceNumber: seqRef.current++,
        runId: 'demo-flight',
      };
    },
    [selectedProject]
  );

  const buildOperatingMessage = useCallback(
    (state: string): MessageInstance => ({
      id: generateId(),
      messageType: 104,
      standard: 'STANAG 4586',
      name: 'Vehicle Operating States',
      source: 'AIR_VEHICLE',
      target: 'CUCS',
      direction: Direction.RX,
      timestamp: new Date().toISOString(),
      fields: {
        vehicleId: 'UAV-001',
        operatingState: state,
        fuelLevelPercent: 85,
      },
      projectId: selectedProject?.id,
      environment: selectedProject?.environment ?? 'development',
      sequenceNumber: seqRef.current++,
      runId: 'demo-flight',
    }),
    [selectedProject]
  );

  const buildAlertMessage = useCallback(
    (message: string, severity: string): MessageInstance => ({
      id: generateId(),
      messageType: 1100,
      standard: 'STANAG 4586',
      name: 'Subsystem Status Alert',
      source: 'VSM',
      target: 'CUCS',
      direction: Direction.RX,
      timestamp: new Date().toISOString(),
      fields: {
        vehicleId: 'UAV-001',
        subsystemId: 'PAYLOAD-01',
        alertLevel: severity,
        description: message,
      },
      projectId: selectedProject?.id,
      environment: selectedProject?.environment ?? 'development',
      sequenceNumber: seqRef.current++,
      runId: 'demo-flight',
    }),
    [selectedProject]
  );

  const maybeSaveToFirestore = useCallback(
    async (instance: MessageInstance) => {
      if (!recordToFirestore || !authUser || !selectedProject) return;
      const event: MessageEvent = {
        ...instance,
        projectId: selectedProject.id,
        environment: selectedProject.environment,
        source: instance.source,
        target: instance.target,
        sourceRole: instance.source,
        targetRole: instance.target,
        payload: instance.fields,
        validation: 'PASS',
        validationStatus: 'PASS',
        validationResults: {},
        createdBy: authUser.uid,
        metadata: { runId: 'demo-flight' },
        createdAt: instance.timestamp,
      };
      try {
        await messageEventRepository.save(event);
      } catch (err) {
        console.error('Demo flight Firestore save failed:', err);
      }
    },
    [recordToFirestore, authUser, selectedProject, messageEventRepository]
  );

  const startDemoCallback = useCallback(() => {
    if (waypoints.length < 2) return;
    startDemo();
    startTimeRef.current = Date.now();
    seqRef.current = 1;

    const tick = () => {
      const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
      if (elapsed > DEMO_MAX_DURATION_MS) {
        stopDemo();
        return;
      }
      const progress = (elapsed % DEMO_MAX_DURATION_MS) / DEMO_MAX_DURATION_MS;
      const position = positionAtProgress(progress);
      const inertial = buildInertialMessage(position);
      addMessage(inertial);
      void maybeSaveToFirestore(inertial);

      if (Math.floor(elapsed / 1000) === 10) {
        const operating = buildOperatingMessage('PATROL');
        addMessage(operating);
        void maybeSaveToFirestore(operating);
      }
      if (Math.floor(elapsed / 1000) === 25) {
        const alert = buildAlertMessage('페이로드 온도 상승', 'WARN');
        addMessage(alert);
        void maybeSaveToFirestore(alert);
      }
      if (Math.floor(elapsed / 1000) === 40) {
        const alert = buildAlertMessage('통신 품질 저하', 'CRITICAL');
        addMessage(alert);
        void maybeSaveToFirestore(alert);
      }
    };

    tick();
    intervalRef.current = setInterval(tick, DEMO_INTERVAL_MS);
  }, [
    addMessage,
    buildInertialMessage,
    buildOperatingMessage,
    buildAlertMessage,
    maybeSaveToFirestore,
    positionAtProgress,
    startDemo,
    stopDemo,
    waypoints.length,
  ]);

  useEffect(() => {
    if (!demoRunning && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [demoRunning]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return { startDemo: startDemoCallback };
};
